import {createContext, useState, useCallback, useContext, useMemo} from "react";
import jwt_decode from "jwt-decode";
import {useHistory} from "react-router-dom";
import ConfigContext from "./ConfigContext";
import {useSnackbar} from "notistack";
import axios from "axios";
import {hasPermission} from "../permissions/hasPermission";

const AuthContext = createContext();

export default AuthContext;

const decodeAccessToken = (tokens) => {
    if (!tokens?.access) return null
    try {
        return jwt_decode(tokens.access)
    } catch (e) {
        return null
    }
}

export const AuthProvider = ({children}) => {
    const {baseUrl} = useContext(ConfigContext)
    const [authTokens, setAuthTokens] = useState(() => {
        const stored = localStorage.getItem("authTokens")
        return stored ? JSON.parse(stored) : null
    });
    // setup_complete is purely a UX shortcut (skip the "finish your card registration" redirect) -
    // it never unlocks any permission, so unlike role/subscriptionPlan/permissions below it's fine
    // for it to be a plain, independently-settable flag.
    const [setupCompleteOverride, setSetupCompleteOverride] = useState(() => localStorage.getItem("setup_complete_override") === 'true')
    const [authLoading, setAuthLoading] = useState(false)
    const history = useHistory();
    const snackbar = useSnackbar()

    // user, role, subscriptionPlan and permissions are derived on the fly from the signed JWT
    // payload on every render - never read from their own writable localStorage entry. Otherwise
    // anyone with devtools access could grant themselves another role or plan just by editing
    // localStorage, since those old keys were trusted as-is without re-checking the token.
    const user = useMemo(() => decodeAccessToken(authTokens), [authTokens])
    const role = user?.role
    const subscriptionPlan = user?.subscription_plan
    const permissions = useMemo(() => user?.permissions || [], [user])
    const setupComplete = useMemo(() => {
        if (setupCompleteOverride) return true
        const raw = user?.setup_complete
        if (raw === undefined || raw === null) return true
        return raw !== false && raw !== 'false'
    }, [setupCompleteOverride, user])

    const api = axios.create({
        baseURL: baseUrl,
        headers: {"Content-Type": "application/json"}
    })

    const setAuthStorage = (data) => {
        setAuthTokens(data);
        localStorage.setItem("authTokens", JSON.stringify(data));
        return decodeAccessToken(data)?.role
    }

    const loginUser = (data) => {
        api.post('api/token/', JSON.stringify(data))
            .then(r => {
                const data = r?.data
                const role = setAuthStorage(data)
                if (!hasPermission(role, 'dashboard', 'view')) history.push('/reservations');
                else history.push("/");
            })
            .catch(e => {
                const status = e?.response?.status
                if (status === 401) snackbar.enqueueSnackbar('Hiba, helytelen email vagy jelszó!', {variant: 'error'})
                else snackbar.enqueueSnackbar('Hiba a bejelentkezésnél, próbálja újra később!', {variant: 'error'})
                console.log(e)
            })
    }

    const registerUser = (data) => {
        if (data.password !== data.password_again) {
            snackbar.enqueueSnackbar("Nem egyezik a két jelszó!", {variant: 'error'})
            return
        }
        setAuthLoading(true)
        api.post('register_new/', JSON.stringify(data))
            .then(r => {
                setAuthLoading(false)
                history.push('/success')
            })
            .catch(e => {
                setAuthLoading(false)
                const message = e?.response?.data?.message
                if (message) snackbar.enqueueSnackbar(message, {variant: 'error'})
                else snackbar.enqueueSnackbar('Váratlan hiba!', {variant: 'error'})
                console.log(e)
            })
    }

    const registerExistingUser = (data) => {
        if (data?.password !== data?.password_again) {
            snackbar.enqueueSnackbar('Nem egyezik a két jelszó!', {variant: 'error'})
            return
        }
        api.post('register_for_business/', JSON.stringify(data))
            .then(r => {
                setAuthLoading(false)
                history.push('/login')
            })
            .catch(e => {
                setAuthLoading(false)
                const message = e?.response?.data?.message
                if (message) snackbar.enqueueSnackbar(message, {variant: 'error'})
                else snackbar.enqueueSnackbar('Váratlan hiba!', {variant: 'error'})
                console.log(e)
            })
    }

    const logoutUser = useCallback(() => {
        setAuthTokens(null)
        setSetupCompleteOverride(false)
        localStorage.removeItem("authTokens")
        localStorage.removeItem("setup_complete_override")
        // legacy keys from the old, locally-writable auth model - clean up if still present
        localStorage.removeItem("permissions")
        localStorage.removeItem("setup_complete")
        localStorage.removeItem("subscription")
        localStorage.removeItem("role")
    }, []);

    const updateToken = useCallback(() => {
        return api.post('api/token/refresh/', JSON.stringify({refresh: authTokens?.refresh}))
            .then(r => {
                const data = r?.data
                setAuthStorage(data)
                return data.access
            })
            .catch(e => {
                logoutUser()
                throw e
            })
    }, [authTokens?.refresh, logoutUser])

    const forgotPassword = (data) => {
        setAuthLoading(true)
        api.post('password/send_link/', JSON.stringify(data))
            .then(r => {
                setAuthLoading(false)
                snackbar.enqueueSnackbar('Email kiküldve!', {variant: 'success'})
                history.push("/")
            })
            .catch(e => {
                setAuthLoading(false)
                snackbar.enqueueSnackbar('Hiba történt!', {variant: 'error'})
                console.log(e)
            })
    }

    const newPassword = (data) => {
        setAuthLoading(true)
        api.post('password/change_password/', JSON.stringify(data))
            .then(r => {
                setAuthLoading(false)
                snackbar.enqueueSnackbar('Sikeresen megváltoztatva!', {variant: 'success'})
                history.push("/login")
            })
            .catch(e => {
                setAuthLoading(true)
                snackbar.enqueueSnackbar('Hiba történt!', {variant: 'error'})
                console.log(e)
            })
    }

    const isSetupComplete = () => setupComplete

    const setSetupComplete = (value) => {
        const complete = Boolean(value)
        setSetupCompleteOverride(complete)
        if (complete) localStorage.setItem("setup_complete_override", 'true')
        else localStorage.removeItem("setup_complete_override")
    }

    const contextData = {
        user: user,
        authTokens: authTokens,
        loginUser: loginUser,
        registerUser: registerUser,
        registerExistingUser: registerExistingUser,
        logoutUser: logoutUser,
        permissions: permissions,
        forgotPassword: forgotPassword,
        newPassword: newPassword,
        setupComplete: setupComplete,
        isSetupComplete: isSetupComplete,
        setSetupComplete: setSetupComplete,
        subscriptionPlan: subscriptionPlan,
        authLoading: authLoading,
        setAuthLoading: setAuthLoading,
        role: role,
        updateToken: updateToken
    };

    return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};
